import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { reviewsApi } from "@/lib/reviewsApi";
import type { ReviewRating } from "@/lib/types";

interface ReviewDialogProps {
  orderId: string | null;
  existingReview?: {
    rating: number;
    comment?: string;
  } | null;
  onSuccess?: () => void;
}

export function ReviewDialog({ orderId, existingReview, onSuccess }: ReviewDialogProps) {
  const { t } = useTranslation();
  const isRtl = useTranslation().language === 'ar';
  const [rating, setRating] = useState<ReviewRating>(0);
  const [comment, setComment] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const queryClient = useQueryClient();

  // Reset form when orderId changes or existingReview changes
  useEffect(() => {
    if (orderId) {
      if (existingReview) {
        setRating(existingReview.rating as ReviewRating);
        setComment(existingReview.comment || "");
      } else {
        setRating(0);
        setComment("");
      }
      setError(null);
      setSuccess(false);
    }
  }, [orderId, existingReview]);

  const handleSubmit = async () => {
    if (rating === 0) {
      setError(t("reviews.ratingRequired"));
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const result = await reviewsApi.submitOrderReview(orderId!, { rating, comment });

      // Mark the order as rated right away, then refetch so the detail view and
      // the orders list pick up the reviewId the server stored. The '/orders'
      // prefix must match the query keys in use-orders.ts.
      queryClient.setQueryData(
        ["/orders", orderId],
        (old: any) => (old ? { ...old, reviewId: result.id } : old)
      );
      queryClient.invalidateQueries({ queryKey: ["/orders"] });

      setSuccess(true);
      setTimeout(() => {
        onSuccess?.();
      }, 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("reviews.submitError"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRatingClick = (value: ReviewRating) => {
    setRating(value);
    setError(null);
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onSuccess?.();
    }
  };

  if (!orderId) return null;

  return (
    <Dialog open={!!orderId} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("reviews.submitReview")}</DialogTitle>
          <DialogDescription>
            {existingReview
              ? t("reviews.updateReview")
              : t("reviews.writeReview")}
          </DialogDescription>
        </DialogHeader>

        {error && (
          <div className="mb-4 p-3 bg-destructive/10 text-destructive rounded-lg text-sm">
            {error}
          </div>
        )}

        {success ? (
          <div className="text-center py-6">
            <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <Star className="w-6 h-6 text-white fill-current" />
            </div>
            <p className="text-lg font-semibold text-green-600 dark:text-green-400 mb-2">
              {t("reviews.reviewSubmitted")}
            </p>
            <p className="text-sm text-muted-foreground">
              {t("reviews.reviewPendingApproval")}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Star Rating */}
            <div>
              <label className="text-sm font-medium mb-2 block">
                {t("reviews.yourRating")}
              </label>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((value) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => handleRatingClick(value as ReviewRating)}
                    className="flex items-center gap-1 transition-transform hover:scale-110"
                  >
                    <Star
                      className={cn(
                        "w-6 h-6 transition-colors",
                        rating >= value
                          ? "fill-yellow-400 text-yellow-400"
                          : "fill-muted text-muted-foreground"
                      )}
                    />
                  </button>
                ))}
              </div>
            </div>

            {/* Comment */}
            <div>
              <label className="text-sm font-medium mb-2 block">
                {t("reviews.yourComment")}{" "}
                <span className="text-xs text-muted-foreground font-normal">
                  ({t("reviews.optional")})
                </span>
              </label>
              <Textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder={t("reviews.commentPlaceholder")}
                rows={3}
                className={cn("resize-none", isRtl && "text-right")}
                disabled={isSubmitting}
              />
            </div>

            {/* Submit Button */}
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting || rating === 0}
              className="w-full"
            >
              {isSubmitting ? t("reviews.submitting") : t("reviews.submit")}
            </Button>

            {/* Update warning for existing review */}
            {existingReview && (
              <p className="text-xs text-muted-foreground text-center mt-2">
                {t("reviews.updateWarning")}
              </p>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
